import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SelectThemeProvider, useSelectTheme } from "@/contexts/SelectThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Nav from "@/components/Nav";
import ComplianceGate from "@/components/ComplianceGate";
import EmailVerifyGate from "@/components/EmailVerifyGate";
import AuthGate from "@/pages/AuthGate";
import Landing from "@/pages/Landing";
import Chooser from "@/pages/Chooser";
import Catalog from "@/pages/Catalog";
import Catalog10VK from "@/pages/Catalog10VK";
import SelectStory from "@/pages/SelectStory";
import BuildAKit from "@/pages/BuildAKit";
import ProductDetail from "@/pages/ProductDetail";
import CoaViewer from "@/pages/CoaViewer";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import PaymentInstructions from "@/pages/PaymentInstructions";
import Orders from "@/pages/Orders";
import Notifications from "@/pages/Notifications";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import AdminOrderWorkspace from "@/pages/AdminOrderWorkspace";
import VerifyEmail from "@/pages/VerifyEmail";
import Refunds from "@/pages/Refunds";
import Disclaimer from "@/pages/Disclaimer";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Shipping from "@/pages/Shipping";
import PolicyPlaceholder from "@/pages/PolicyPlaceholder";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Unsubscribe from "@/pages/Unsubscribe";
import PaymentSuccess from "@/pages/PaymentSuccess";
import OrderSlip from "@/pages/OrderSlip";
import OrderDetail from "@/pages/OrderDetail";
import Reorder from "@/pages/Reorder";
import SupplyMockupCloakedCard from "@/pages/SupplyMockupCloakedCard";
import SupplyMockupAmberPicker from "@/pages/SupplyMockupAmberPicker";
import PreviewSequenceVariants from "@/pages/PreviewSequenceVariants";
import PreviewSupplyTiers from "@/pages/PreviewSupplyTiers";
import SupplyRedesignMockup from "@/pages/SupplyRedesignMockup";
import AdminPriceComparisons from "@/pages/AdminPriceComparisons";
import AdminSupplyTiers from "@/pages/AdminSupplyTiers";
import PreviewAdminSupplyTiers from "@/pages/PreviewAdminSupplyTiers";
import PreviewAdminProductEditor from "@/pages/PreviewAdminProductEditor";
import PreviewSupplyFourCards from "@/pages/PreviewSupplyFourCards";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import { Toaster } from "@/components/ui/sonner";

function ShellLayout() {
  const { user } = useAuth();
  const { isSelect } = useSelectTheme();
  const isAdmin = user?.role === "admin";
  const needsVerification = user && !isAdmin && !user.email_verified;
  const needsCompliance = user && !isAdmin && user.email_verified && !user.compliance_acknowledged;
  return (
    <div className={isSelect ? "bg-[#F1E5C7] min-h-screen" : ""} data-select-theme={isSelect ? "on" : "off"}>
      <AnnouncementBanner />
      <Nav />
      <Outlet />
      {needsVerification && <EmailVerifyGate />}
      {needsCompliance && <ComplianceGate />}
      <footer className={`mt-24 border-t transition-colors ${isSelect ? "border-[#D6C8A3] bg-[#F1E5C7]" : "border-borderSubtle"}`}>
        <div className={`max-w-[1400px] mx-auto px-6 lg:px-12 py-8 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] ${isSelect ? "text-[#B08A45]" : "text-stone"}`}>
          <Link to="/faq" className={isSelect ? "hover:text-[#DDA94E]" : "hover:text-charcoal"} data-testid="footer-faq">FAQ</Link>
          <span className={isSelect ? "text-[#2A2620]" : "text-borderSubtle"}>·</span>
          <Link to="/contact" className={isSelect ? "hover:text-[#DDA94E]" : "hover:text-charcoal"} data-testid="footer-contact">Contact</Link>
          <span className={isSelect ? "text-[#2A2620]" : "text-borderSubtle"}>·</span>
          <Link to="/policies/refunds" className={isSelect ? "hover:text-[#DDA94E]" : "hover:text-charcoal"} data-testid="footer-refunds">Returns &amp; Refunds</Link>
          <span className={isSelect ? "text-[#2A2620]" : "text-borderSubtle"}>·</span>
          <Link to="/policies/disclaimer" className={isSelect ? "hover:text-[#DDA94E]" : "hover:text-charcoal"} data-testid="footer-disclaimer">Research-Use Disclaimer</Link>
          <span className={isSelect ? "text-[#2A2620]" : "text-borderSubtle"}>·</span>
          <a
            href="mailto:support@bulkbuykits.com"
            className={`normal-case tracking-normal ${isSelect ? "text-[#F1E5C7] hover:text-[#DDA94E]" : "hover:text-oxblood text-charcoal"}`}
            data-testid="footer-contact-email"
          >
            support@bulkbuykits.com
          </a>
        </div>
        <div className={`hairline max-w-[900px] mx-auto ${isSelect ? "border-[#2A2620]" : ""}`} />
        <div className={`max-w-[900px] mx-auto text-center text-[11px] px-6 py-8 space-y-3 leading-relaxed ${isSelect ? "text-[#B08A45]" : "text-stone"}`}>
          <p><span className={isSelect ? "text-[#F1E5C7]" : "text-charcoal"}>Disclaimer:</span> All products offered on this site are intended for research and laboratory use only and are not for human consumption in any form.</p>
          <p>Statements on this website have not been evaluated by the FDA and are not intended to diagnose, treat, cure, or prevent any disease.</p>
          <p className={`font-mono uppercase tracking-[0.22em] pt-2 ${isSelect ? "text-[#B08A45]" : ""}`}>© {new Date().getFullYear()} Bulk Buy Kits {isSelect && <span className="ml-2 text-[#DDA94E]">· Research 1617</span>}</p>
        </div>
      </footer>
    </div>
  );
}

function Root() {
  const { user, checked } = useAuth();
  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone">
        <p className="overline">Loading…</p>
      </div>
    );
  }
  // Logged-in users go to the chooser; admins skip straight to the operator console;
  // temp-viewers land directly on the (filtered) shop.
  if (user) {
    const dest = user.role === "admin" ? "/admin" : user.role === "temp_viewer" ? "/select" : "/chooser";
    return <Navigate to={dest} replace />;
  }
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <SelectThemeProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/" element={<Root />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/enter" element={<AuthGate />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/preview/cloaked-card" element={<SupplyMockupCloakedCard />} />
              <Route path="/preview/sequence-variants" element={<PreviewSequenceVariants />} />
              <Route path="/preview/supply-tiers" element={<PreviewSupplyTiers />} />
              <Route path="/preview/admin-supply-tiers" element={<PreviewAdminSupplyTiers />} />
              <Route path="/preview/admin-product-editor" element={<PreviewAdminProductEditor />} />
              <Route path="/preview/supply-four-cards" element={<PreviewSupplyFourCards />} />
              <Route path="/admin/price-comparisons" element={
                <ProtectedRoute adminOnly><AdminPriceComparisons /></ProtectedRoute>
              } />
              <Route path="/admin/supply-tiers" element={
                <ProtectedRoute adminOnly><AdminSupplyTiers /></ProtectedRoute>
              } />

              <Route path="/chooser" element={<ProtectedRoute blockTempViewer><Chooser /></ProtectedRoute>} />
              <Route path="/select" element={<ProtectedRoute><SelectStory /></ProtectedRoute>} />
              <Route path="/select-preview" element={<ProtectedRoute><SelectStory /></ProtectedRoute>} />

              <Route element={<ProtectedRoute><ShellLayout /></ProtectedRoute>}>
                <Route path="/shop" element={<Catalog />} />
                <Route path="/shop/10-vial" element={<Catalog10VK />} />
                <Route path="/shop/build" element={<ProtectedRoute blockTempViewer><BuildAKit /></ProtectedRoute>} />
                <Route path="/shop/:slug" element={<ProductDetail />} />
                <Route path="/coa/:slug" element={<CoaViewer />} />
                <Route path="/cart" element={<ProtectedRoute blockTempViewer><Cart /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute blockTempViewer><Checkout /></ProtectedRoute>} />
                <Route path="/payment/:orderId" element={<ProtectedRoute blockTempViewer><PaymentInstructions /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute blockTempViewer><Orders /></ProtectedRoute>} />
                <Route path="/orders/:orderId/slip" element={<ProtectedRoute blockTempViewer><OrderSlip /></ProtectedRoute>} />
                <Route path="/orders/:orderId" element={<ProtectedRoute blockTempViewer><OrderDetail /></ProtectedRoute>} />
                <Route path="/reorder/:orderId" element={<ProtectedRoute blockTempViewer><Reorder /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute blockTempViewer><Notifications /></ProtectedRoute>} />
                <Route path="/account" element={<ProtectedRoute blockTempViewer><Account /></ProtectedRoute>} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/policies/refunds" element={<Refunds />} />
                <Route path="/policies/disclaimer" element={<Disclaimer />} />
                <Route path="/policies/shipping" element={<Shipping />} />
                <Route path="/policies/privacy" element={<Privacy />} />
                <Route path="/policies/terms" element={<Terms />} />
                <Route path="/admin" element={
                  <ProtectedRoute adminOnly><Admin /></ProtectedRoute>
                } />
                {/* iter185 Phase 2 — Individual Order Workspace */}
                <Route path="/admin/orders/:orderId" element={
                  <ProtectedRoute adminOnly><AdminOrderWorkspace /></ProtectedRoute>
                } />
                <Route path="/admin/mockups/amber-picker" element={
                  <ProtectedRoute adminOnly><SupplyMockupAmberPicker /></ProtectedRoute>
                } />
                <Route path="/admin/mockups/supply-redesign" element={
                  <ProtectedRoute adminOnly><SupplyRedesignMockup /></ProtectedRoute>
                } />
                <Route path="/admin/mockups/cloaked-card" element={
                  <ProtectedRoute adminOnly><SupplyMockupCloakedCard /></ProtectedRoute>
                } />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {/* iter_142 — global "10% off" tab + popup, mounted OUTSIDE Routes
                so it appears on every page (landing, /enter, /shop, /admin,
                /checkout, etc.). Auto-open still gated to landing route only
                via useLocation() inside the component. */}
          </BrowserRouter>
          <Toaster position="top-right" richColors offset="88px" />
          </SelectThemeProvider>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
